import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../../api/header";
import apiClient from "../../../api/apiClient";
import { toast } from "sonner";
import { all_routes } from "../../../Router/all_routes";

// UI Components
import { PageContainer } from "@/components/layout/PageContainer";
import { PageHeader, PageHeaderHeading } from "@/components/ui/page-header";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ReactSelect } from "@/components/ui/react-select";
import { Textarea } from "@/components/ui/textarea";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Icons
import { Save, ArrowLeft, FileText } from "lucide-react";

const ConvertAssembleJobToQuotation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    valid_until: "",
    notes: "",
    discount_type: null,
    discount_value: "",
  });

  // Fetch job details for display
  const { data: jobDetails, isLoading: isJobLoading } = useQuery({
    queryKey: ["assemble-job", id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.ASSEMBLE.JOB_DETAILS(id));
      return res.data?.data;
    },
    enabled: !!id,
  });

  // Compute discount_amount from type + value + job total for the payload
  const computeDiscountAmount = () => {
    if (!formData.discount_value || !formData.discount_type) return null;
    const dv = parseFloat(formData.discount_value);
    if (isNaN(dv) || dv < 0) return null;
    if (formData.discount_type === "fixed") {
      return parseFloat(dv.toFixed(2));
    }
    if (formData.discount_type === "percentage") {
      const subtotal = parseFloat(jobDetails?.total_price || 0);
      return parseFloat(((dv / 100) * subtotal).toFixed(2));
    }
    return null;
  };

  const convertMutation = useMutation({
    mutationFn: async (payload) => {
      return await apiClient.post(
        API_ENDPOINTS.ASSEMBLE.JOB_CONVERT_TO_QUOTATION(id),
        payload,
      );
    },
    onSuccess: async () => {
      toast.success("Job successfully converted to quotation!");
      await queryClient.invalidateQueries({
        queryKey: ["quotations"],
        refetchType: "all",
      });
      navigate(all_routes.assembleJobs);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to convert to quotation",
      );
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();

    const discountAmount = computeDiscountAmount();

    const payload = {
      valid_until: formData.valid_until || null,
      notes: formData.notes || null,
      discount_type: formData.discount_type || null,
      discount_value: formData.discount_value
        ? parseFloat(formData.discount_value)
        : null,
      discount_amount: discountAmount,
    };

    convertMutation.mutate(payload);
  };

  if (isJobLoading) {
    return <div className="p-8 text-center">Loading job details...</div>;
  }

  return (
    <PageContainer>
      <PageHeader>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <PageHeaderHeading
            title="Convert to Quotation"
            subtitle={`Converting Assemble Job #${jobDetails?.job_number || id}`}
          />
        </div>
      </PageHeader>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Job summary */}
        {jobDetails && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Job Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Job Number</p>
                  <p className="font-medium">{jobDetails.job_number || "-"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <p className="font-medium capitalize">
                    {jobDetails.status?.label ?? jobDetails.status?.value ?? jobDetails.status ?? "-"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Price</p>
                  <p className="font-medium">
                    {jobDetails.total_price != null
                      ? Number(jobDetails.total_price).toLocaleString(
                        undefined,
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                      )
                      : "-"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Config</p>
                  <p className="font-medium">
                    {jobDetails.assemble_config?.name || "-"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quotation fields */}
        <Card>
          <CardHeader>
            <CardTitle>Quotation Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Valid Until */}
              <div className="space-y-2">
                <Label htmlFor="valid_until">Valid Until</Label>
                <Input
                  id="valid_until"
                  type="datetime-local"
                  value={formData.valid_until}
                  onChange={(e) =>
                    setFormData({ ...formData, valid_until: e.target.value })
                  }
                />
              </div>

              {/* Discount Type */}
              <div className="space-y-2">
                <Label htmlFor="discount_type">Discount Type</Label>
                <ReactSelect
                  inputId="discount_type"
                  isClearable
                  value={
                    formData.discount_type
                      ? {
                        value: formData.discount_type,
                        label:
                          formData.discount_type === "fixed"
                            ? "Fixed Amount"
                            : "Percentage",
                      }
                      : null
                  }
                  onChange={(option) =>
                    setFormData({
                      ...formData,
                      discount_type: option?.value || null,
                      discount_value: option ? formData.discount_value : "",
                    })
                  }
                  options={[
                    { value: "fixed", label: "Fixed Amount" },
                    { value: "percentage", label: "Percentage" },
                  ]}
                  placeholder="Select discount type..."
                />
              </div>

              {/* Discount Value */}
              <div className="space-y-2">
                <Label htmlFor="discount_value">
                  Discount Value
                  {formData.discount_type === "percentage" && " (%)"}
                  {formData.discount_type === "fixed" && " (Amount)"}
                </Label>
                <Input
                  id="discount_value"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={formData.discount_value}
                  disabled={!formData.discount_type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      discount_value: e.target.value,
                    })
                  }
                />
                {formData.discount_type &&
                  formData.discount_value &&
                  jobDetails?.total_price != null && (
                    <p className="text-xs text-muted-foreground">
                      Computed discount amount:{" "}
                      {Number(computeDiscountAmount() ?? 0).toLocaleString(
                        undefined,
                        { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                      )}
                    </p>
                  )}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Additional quotation notes..."
                rows={3}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-end pt-6 border-t mt-4">
            <Button type="submit" disabled={convertMutation.isPending}>
              <Save className="mr-2 h-4 w-4" />
              {convertMutation.isPending
                ? "Converting..."
                : "Convert to Quotation"}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </PageContainer>
  );
};

export default ConvertAssembleJobToQuotation;
